import { BackendLimits } from '@/components/constants/backend-limits';

/**
 * Splits an array into chunks of specified size.
 * @param {Array} array - Array to split
 * @param {number} maxSize - Maximum items per chunk
 * @returns {Array} Array of chunk arrays
 */
export function chunkArray(array, maxSize) {
  if (!Array.isArray(array) || array.length === 0) {
    return [array];
  }
  if (array.length <= maxSize) {
    return [array];
  }
  const chunks = [];
  for (let i = 0; i < array.length; i += maxSize) {
    chunks.push(array.slice(i, i + maxSize));
  }
  return chunks;
}

/**
 * Determines if chunking is needed.
 * @param {Array} array - Array to check
 * @param {number} limit - Maximum items allowed
 * @returns {boolean} True if chunking is needed
 */
export function needsChunking(array, limit) {
  return Array.isArray(array) && array.length > limit;
}

/**
 * Executes chunked requests sequentially with error handling.
 * @param {Object} options - Execution options
 * @param {Array} options.items - Items to process
 * @param {number} options.chunkSize - Maximum items per chunk
 * @param {Function} options.requestFn - Function to execute for each chunk
 * @param {Function} [options.aggregateFn] - Function to aggregate results (default: flatten)
 * @param {Object} [options.errorStrategy] - Error handling strategy
 * @param {boolean} [options.errorStrategy.stopOnError=true] - Stop processing on first error
 * @returns {Promise<Object>} Result object with success status, data, and error info
 */
export async function executeChunkedRequests({
  items,
  chunkSize,
  requestFn,
  aggregateFn = (results) => results.flat(),
  errorStrategy = { stopOnError: true },
}) {
  if (!needsChunking(items, chunkSize)) {
    const result = await requestFn(items);
    return {
      success: true,
      data: result,
      chunksProcessed: 1,
      totalChunks: 1,
      errors: [],
    };
  }

  const chunks = chunkArray(items, chunkSize);
  const results = [];
  const errors = [];

  for (let i = 0; i < chunks.length; i++) {
    try {
      const chunkResult = await requestFn(chunks[i]);
      results.push(chunkResult);
    } catch (error) {
      errors.push({ chunkIndex: i, items: chunks[i], error });

      if (errorStrategy.stopOnError) {
        return {
          success: false,
          data: aggregateFn(results),
          chunksProcessed: i,
          totalChunks: chunks.length,
          errors,
          partialSuccess: results.length > 0,
        };
      }
    }
  }

  return {
    success: errors.length === 0,
    data: aggregateFn(results),
    chunksProcessed: chunks.length,
    totalChunks: chunks.length,
    errors,
    partialSuccess: errors.length > 0 && results.length > 0,
  };
}

export { BackendLimits };
